import {
	QueryClient,
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import axios, { AxiosError, AxiosProgressEvent } from 'axios'
import { DocumentService } from '../../lib/api/document.services'
import { TaskService } from '../../lib/api/task.services'
import {
	FileUploadProps,
	FolderDataActionProps,
	GetContentProps,
	GetDeletedDocumentsDataResponse,
	GetDocumentsDataResponse,
	GetFileChatsDataResponse,
	GetPreviewDataResponse,
	GetRecentFilesResponse,
	GetDocumentDataResponse,
	RefreshFilePayload,
	GetFolderDetailsDataResponse,
	GetSearchParamsProps,
	GetSearchData,
	GetThumbnailDataResponse,
	GetFileMetadataDataResponse,
	UpdateMetadataProps,
	TableDataDefaultGetParamsProps,
	DownloadFileParamsProps,
	DocumentData,
	CreateFileDataProps,
	MultiReclassifyObjectsDataProps,
	ShareFileDataProps,
	GetSharedPreviewDataResponse,
	GetSharedContentProps,
} from '../../lib/api/types/document.interfaces'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import toastCaller from '../../lib/utils/toastCaller'
import { handleApiError } from '../../lib/utils/handleApiError'
import { useState, useEffect, useContext } from 'react'
import { SignalRContext } from '../../context/signalrContext'

export const invalidateDocumentsCache = (
	queryClient: QueryClient,
	canUseFolderEvents?: (id: string) => boolean,
) => {
	queryClient.invalidateQueries({
		queryKey: ['documents'],
		predicate: (query) => !canUseFolderEvents?.(query.queryKey[1] as string),
	})
	queryClient.invalidateQueries({ queryKey: ['documents-items'], exact: false })
	queryClient.invalidateQueries({ queryKey: ['get-folder'], exact: false })
	queryClient.invalidateQueries({ queryKey: ['recent', 'documents'] })
	queryClient.invalidateQueries({ queryKey: ['favorites', 'documents'] })
	queryClient.invalidateQueries({ queryKey: ['deleted', 'documents'] })
	queryClient.invalidateQueries({ queryKey: ['items'] })
}

const getApiErrorMessage = (error: any): string | undefined =>
	error?.response?.data?.errors?.[0] ||
	error?.response?.data?.errorMessageCode ||
	error?.response?.data?.message ||
	error?.message

const isCanceledRequest = (error: unknown) =>
	axios.isCancel(error) ||
	(error instanceof AxiosError && error.code === 'ERR_CANCELED')

export const openAuraEdit = (fileId: string, fileName?: string) => {
	const nameQuery = fileName ? `&name=${encodeURIComponent(fileName)}` : ''
	window.location.assign(`aura://edit?fileId=${fileId}${nameQuery}`)
}

export const useDocuments = (
	{ id }: TableDataDefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetDocumentsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetDocumentsDataResponse>({
		queryKey: ['items'],
		queryFn: () => DocumentService.getDocumentsByFolderId({ id }),
		enabled: isAuthenticated && Boolean(id),
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

export const useGetFolder = (
	{ id, ...params }: TableDataDefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetFolderDetailsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetFolderDetailsDataResponse>({
		queryKey: id ? ['get-folder', id] : [],
		queryFn: () => DocumentService.getFolderDetails({ id, ...params }),
		enabled: isAuthenticated && Boolean(id),
		retry: (failureCount, error) => {
			const axiosError = error as AxiosError
			if (axiosError?.response?.status && axiosError.response.status >= 500) {
				return false
			}
			return failureCount < 1
		},
		...options,
	})
}

export const useFolderDetails = (
	{ id, ...params }: TableDataDefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetFolderDetailsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetFolderDetailsDataResponse>({
		queryKey: id ? ['documents', id] : [],
		queryFn: () => DocumentService.getFolderDetails({ id, ...params }),
		enabled: isAuthenticated && Boolean(id),
		retry: (failureCount, error) => {
			const axiosError = error as AxiosError
			if (axiosError?.response?.status && axiosError.response.status >= 500) {
				return false
			}
			if (failureCount === 1) {
				handleApiError(error)
			}

			return failureCount < 1
		},
		refetchOnWindowFocus: false,
		...options,
	})
}

export const useUpdateFolderDetailsItems = ({
	id,
	offset = 0,
	limit = 10,
	columnFilters,
	...params
}: TableDataDefaultGetParamsProps) => {
	const queryClient = useQueryClient()
	const baseKey = ['documents-items']
	const itemsKey = [
		id,
		limit,
		JSON.stringify(columnFilters),
		JSON.stringify(params),
	]

	const getPageKey = (forcedOffset: number) => [
		...baseKey,
		...itemsKey,
		forcedOffset,
	]

	const pageSize = limit * 3

	const handleUpdateFolderDetails = (
		fresh: GetDocumentsDataResponse,
		currentOffset: number,
	) => {
		queryClient.setQueryData<GetFolderDetailsDataResponse>(
			['documents', id],
			(oldData) => {
				if (!oldData) return oldData

				const pag = fresh.data.pagination
				return {
					...oldData,
					data: {
						...oldData.data,
						items: {
							entries: fresh?.data?.entries?.slice(
								currentOffset - pag.offset,
								currentOffset - pag.offset + limit,
							),
							pagination: {
								...pag,
								offset: currentOffset,
								limit,
							},
						},
					},
				}
			},
		)
	}

	const mutation = useMutation<
		GetDocumentsDataResponse,
		Error,
		{ forcedOffset: number; actualOffset: number; forceRefresh?: boolean }
	>({
		mutationFn: async ({ forcedOffset, forceRefresh }) => {
			const queryKey = getPageKey(forcedOffset)
			const cached =
				queryClient.getQueryData<GetDocumentsDataResponse>(queryKey)

			if (cached && !forceRefresh) {
				return cached
			}

			const fresh = await DocumentService.getDocumentsByFolderId({
				id,
				offset: forcedOffset,
				limit: pageSize,
				columnFilters,
				...params,
			})

			queryClient.setQueryData(queryKey, fresh)
			return fresh
		},
		onSuccess: (fresh, variables) => {
			handleUpdateFolderDetails(fresh, variables.actualOffset)
		},
		onError: (error) => {
			handleApiError(error)
		},
	})

	const updateItems = ({
		offset: currentOffset = offset,
		forceRefresh = false,
	} = {}) => {
		const forcedOffset = Math.floor(currentOffset / pageSize) * pageSize
		const queryKey = getPageKey(forcedOffset)
		const cached = queryClient.getQueryData<GetDocumentsDataResponse>(queryKey)

		if (!cached || forceRefresh) {
			return mutation
				.mutateAsync({
					forcedOffset,
					actualOffset: currentOffset,
					forceRefresh,
				})
				.catch((): undefined => undefined)
		} else {
			handleUpdateFolderDetails(cached, currentOffset)
			return Promise.resolve(cached)
		}
	}

	return {
		updateItems,
		isUpdating: mutation.isPending,
		error: mutation.error,
		isSuccess: mutation.isSuccess,
	}
}
// -- update file in lists (connected to socket) - will be update with folder also.
export const useUpdateDocumentCache = () => {
	const queryClient = useQueryClient()

	const patchFile = (
		old: DocumentData,
		updated: Partial<DocumentData>,
	): DocumentData => ({
		...old,
		...updated,
		name: updated?.name ?? old?.name,
		category: updated?.category ?? old?.category,
		subcategory: updated?.subcategory ?? old?.subcategory,
		processingStatus:
			updated?.processingStatus ??
			updated?.metadata?.processingStatus ??
			old?.processingStatus ??
			0,
		metadata: updated?.metadata ?? old?.metadata,
	})

	const updateFileInFolder = (
		folderId: string,
		updatedFile: Partial<DocumentData> & { id: string },
	) => {
		queryClient.setQueryData<GetFolderDetailsDataResponse>(
			['documents', folderId],
			(oldData) => {
				if (!oldData) return oldData
				const entries = oldData?.data?.items?.entries?.map((entry) =>
					entry.id === updatedFile?.id ? patchFile(entry, updatedFile) : entry,
				)
				return {
					...oldData,
					data: {
						...oldData?.data,
						items: {
							...oldData?.data?.items,
							entries,
						},
					},
				}
			},
		)
	}

	const updateFileInFavorites = (
		updatedFile: Partial<DocumentData> & { id: string },
	) => {
		// Update all favorites queries with different parameters
		queryClient.setQueriesData<GetDocumentsDataResponse>(
			{ queryKey: ['favorites', 'documents'], exact: false },
			(oldData) => {
				if (!oldData) return oldData
				const entries = oldData?.data?.entries?.map((entry) =>
					entry?.id === updatedFile?.id ? patchFile(entry, updatedFile) : entry,
				)
				return {
					...oldData,
					data: {
						...oldData?.data,
						entries,
					},
				}
			},
		)
	}

	const updateFileInRecent = (
		updatedFile: Partial<DocumentData> & { id: string },
	) => {
		// Update all recent documents queries with different parameters
		queryClient.setQueriesData<GetRecentFilesResponse>(
			{ queryKey: ['recent', 'documents'], exact: false },
			(oldData) => {
				if (!oldData) return oldData
				const entries = oldData?.data.map((entry) =>
					entry.id === updatedFile?.id ? patchFile(entry, updatedFile) : entry,
				)
				return {
					...oldData,
					data: entries,
				}
			},
		)
	}

	const updateEverywhere = ({
		updatedFile,
		folderId,
	}: {
		updatedFile: Partial<DocumentData> & { id: string }
		folderId?: string | null
	}) => {
		if (folderId) {
			updateFileInFolder(folderId, updatedFile)
		}
		updateFileInFavorites(updatedFile)
		updateFileInRecent(updatedFile)
	}

	const refreshFile = (
		payload: RefreshFilePayload,
		folderId?: string | null,
	) => {
		const file = payload?.file
		const fileId = file?.id || payload?.fileId
		if (!fileId) return
		void queryClient.invalidateQueries({
			queryKey: ['documentMetadata', fileId],
			exact: true,
		})
		const key = ['file', fileId]
		const previous =
			queryClient.getQueryData<GetDocumentDataResponse>(key)?.data
		if (file?.id) {
			void queryClient.cancelQueries({ queryKey: key, exact: true })
			queryClient.setQueryData<GetDocumentDataResponse>(key, (old) => ({
				...old,
				success: true,
				data: file,
			}))
		} else {
			void queryClient.invalidateQueries({ queryKey: key, exact: true })
		}

		const contentChanged =
			!file ||
			!previous ||
			previous?.version !== file?.version ||
			previous?.versionId !== file?.versionId ||
			previous?.size !== file?.size
		if (contentChanged) {
			void queryClient.resetQueries({
				queryKey: ['documentPreview', fileId, undefined],
				exact: true,
			})
			void queryClient.resetQueries({
				queryKey: ['documentThumbnail', fileId, undefined],
				exact: true,
			})
		}

		if (file?.id) {
			updateEverywhere({
				updatedFile: file,
				folderId: file.parent?.id ?? file.path?.[file.path.length - 1]?.id ?? folderId,
			})
		}
	}

	return { updateEverywhere, refreshFile }
}
// --

export const useRecentFiles = (
	options?: Partial<QueryObserverOptions<GetRecentFilesResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetRecentFilesResponse>({
		queryKey: ['recent', 'documents'],
		queryFn: () => DocumentService.getRecentDocuments(),
		enabled: isAuthenticated,
		staleTime: 1000 * 5,
		// staleTime: 0,
		retry: 1,
		...options,
	})
}

export const useDeletedDocuments = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetDeletedDocumentsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetDeletedDocumentsDataResponse>({
		queryKey: id ? ['deleted', 'documents'] : [],
		queryFn: () => DocumentService.getDeletedDocumentsByFolderId({ id }),
		enabled: isAuthenticated && Boolean(id),
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

export const useCreateContent = () => {
	const mutation = useMutation({
		mutationKey: ['createContent'],
		mutationFn: ({
			size,
			storageKey,
			signal,
		}: {
			size?: number
			storageKey?: string
			signal?: AbortSignal
		}) => DocumentService.createContent({ size, storageKey, signal }),
		onError: (error) => {
			if (!isCanceledRequest(error)) handleApiError(error)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			const result = await mutation.mutateAsync(...params)
			return result
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useContent = (
	{ id, versionId }: GetContentProps,
	options?: Partial<QueryObserverOptions<GetContentProps>>,
) => {
	const { isAuthenticated } = useAuth()
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return useQuery<any>({
		queryKey: ['documentContent', id],
		queryFn: () =>
			versionId
				? DocumentService.getContentByVersionId({ id, versionId } as any)
				: DocumentService.getContentById({ id } as any),
		enabled: isAuthenticated && Boolean(id),
		refetchOnWindowFocus: false,
		retry: 1,
		...options,
	})
}

export const useDownloadFile = () => {
	const mutation = useMutation({
		mutationKey: ['downloadFile'],
		mutationFn: async ({
			id,
			versionId,
			fileName,
			fileType,
			onShowProgress,
		}: DownloadFileParamsProps & {
			onShowProgress?: (
				status: 'pending' | 'processing' | 'completed' | 'failed',
				progress?: number,
				error?: string,
			) => void
		}) => {
			const filename = fileName ?? 'document'

			onShowProgress?.('pending', 0)

			try {
				onShowProgress?.('processing', 0.5)

				const shouldUseVersion =
					typeof versionId === 'string' && versionId.trim() !== ''

				const res = shouldUseVersion
					? await DocumentService.getContentByVersionId({
							id,
							versionId,
						} as any)
					: await DocumentService.getContentById({ id } as any)

				if (!res || !(res instanceof ArrayBuffer) || res.byteLength === 0) {
					throw new Error('File is empty or invalid')
				}

				const mimeType = fileType
					? `application/${fileType}`
					: 'application/octet-stream'
				const blob = new Blob([res], { type: mimeType })
				const url = window.URL.createObjectURL(blob)

				const link = document.createElement('a')
				link.href = url
				link.download = filename
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				window.URL.revokeObjectURL(url)

				onShowProgress?.('completed', 1)
			} catch (error) {
				onShowProgress?.(
					'failed',
					undefined,
					(error as Error).message || 'Download failed',
				)
				throw error
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUploadToUrl = () => {
	const mutation = useMutation({
		mutationKey: ['uploadToUrl'],
		mutationFn: ({
			file,
			uploadUrl,
			onUploadProgress,
			signal,
		}: {
			file: File
			uploadUrl: string
			onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
			signal?: AbortSignal
		}) =>
			DocumentService.uploadToUrl({
				file,
				uploadUrl,
				onUploadProgress,
				signal,
			}),
		onError: (error) => {
			// handleApiError(error)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		return mutation.mutateAsync(...params)
	}

	return { ...mutation, mutateAsync }
}

export const useUploadDocument = () => {
	const queryClient = useQueryClient()
	const socket = useContext(SignalRContext)

	const mutation = useMutation({
		mutationKey: ['uploadDocument'],
		mutationFn: ({
			data,
			signal,
		}: {
			data: FileUploadProps
			invalidate?: boolean
			withoutErrorToast?: boolean
			signal?: AbortSignal
		}) => DocumentService.fileUpload({ data, signal }),
		onError: (error, { withoutErrorToast = false }) => {
			!withoutErrorToast && handleApiError(error)
		},
		onSettled: (_, error, { data: { parentId: id }, invalidate = true }) => {
			if (invalidate && (error || !socket?.canUseFolderEvents(id))) {
				queryClient.invalidateQueries({
					queryKey: ['documents', id],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		return await mutation.mutateAsync(...params)
	}

	return { ...mutation, mutateAsync }
}

// Complete file upload hook - all 3 steps in one hook
export const useCompleteFileUpload = () => {
	const queryClient = useQueryClient()
	const socket = useContext(SignalRContext)
	const { mutateAsync: createContent } = useCreateContent()
	const { mutateAsync: uploadToUrl } = useUploadToUrl()
	const { mutateAsync: uploadDocument } = useUploadDocument()

	const mutation = useMutation({
		mutationKey: ['completeFileUpload'],
		mutationFn: async ({
			file,
			name,
			parentId,
			userId,
			signal,
			onUploadProgress,
		}: {
			file: File
			name: string
			parentId: string
			userId: string
			signal?: AbortSignal
			invalidate?: boolean
			withoutErrorToast?: boolean
			onUploadProgress?: (progressEvent: any) => void
		}) => {
			// Step 1: Get upload URL and storageKey
			const createContentResponse = await createContent({
				size: file.size,
				signal,
			})

			const uploadUrl = createContentResponse?.data?.data?.uploadUrl
			const storageKey = createContentResponse?.data?.data?.storageKey || ''

			if (!uploadUrl || !storageKey) {
				throw new Error('Failed to get upload URL')
			}

			// Step 2: Upload file to S3
			await uploadToUrl({
				file,
				uploadUrl,
				signal,
				onUploadProgress,
			})

			// Step 3: Register document in database
			const uploadResponse = await uploadDocument({
				data: {
					name,
					storageKey,
					parentId,
					userId,
				},
				invalidate: false,
				withoutErrorToast: true,
				signal,
			})

			return uploadResponse
		},
		onError: (error, { withoutErrorToast = false }) => {
			!withoutErrorToast && handleApiError(error)
		},
		onSettled: (_, error, { parentId, invalidate = true }) => {
			if (invalidate && (error || !socket?.canUseFolderEvents(parentId))) {
				queryClient.invalidateQueries({
					queryKey: ['documents', parentId],
				})
			}
		},
	})

	return mutation
}

export const useCreateFolder = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['createFolder'],
		mutationFn: ({
			data,
		}: {
			data: FolderDataActionProps
			invalidate?: boolean
			toastId?: string
			updateToast?: boolean
		}) => DocumentService.createFolder({ data }),

		onError: (error, { data, toastId, updateToast }) =>
			toastCaller({
				type: 'warning',
				errorMessageCode: (
					error as { response?: { data?: { errors?: string[] } } }
				)?.response?.data?.errors?.[0],
				objectName: data?.name,
				toastId,
				update: updateToast,
			}),

		onSuccess: (_, { invalidate = true }) => {
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['documents'],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			const result = await mutation.mutateAsync(...params)
			return result
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useCreateFile = () => {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationKey: ['createFile'],
		mutationFn: async ({
			data,
		}: {
			data: CreateFileDataProps
			invalidate?: boolean
		}) => {
			const response = await DocumentService.createFile({ data })
			if (response?.data?.success) {
				const { id } = response?.data?.data ?? {}
				if (id) {
					const fileName = `${data?.name ?? ''}${data?.extension ?? ''}`
					openAuraEdit(id, fileName)
				}
			} else {
				handleApiError(response)
			}
			return response
		},
		onError: (error, { data }) =>
			toastCaller({
				type: 'warning',
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				errorMessageCode: (error as any)?.response?.data?.errors[0],
				objectName: data?.name,
			}),
		onSuccess: (_, { data, invalidate = true }) => {
			// if (invalidate) {
			// 	queryClient.invalidateQueries({
			// 		queryKey: ['get-folder', data?.],
			// 	})
			// }
			toastCaller({
				type: 'success',
				message: `The file ${data?.name} was created successfully`,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			const result = await mutation.mutateAsync(...params)
			return result
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useGetFileChats = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetFileChatsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetFileChatsDataResponse>({
		queryKey: ['fileChats', id],
		queryFn: () => DocumentService.getFileChat({ id } as any),
		enabled: isAuthenticated && Boolean(id),
		refetchOnWindowFocus: false,
		...options,
	})
}

// --- File info | Metadata ---
export const usePreview = (
	{ id, versionId }: GetContentProps,
	options?: Partial<QueryObserverOptions<GetPreviewDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetPreviewDataResponse>({
		queryKey: ['documentPreview', id, versionId],
		queryFn: () =>
			versionId
				? DocumentService.getPreviewByVersion({ id, versionId } as any)
				: DocumentService.getPreview({ id } as any),
		enabled: isAuthenticated && Boolean(id),
		refetchOnWindowFocus: false,
		...options,
	})
}

export const useThumbnail = (
	{ id, versionId }: GetContentProps,
	options?: Partial<QueryObserverOptions<GetThumbnailDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetThumbnailDataResponse>({
		queryKey: ['documentThumbnail', id, versionId],
		queryFn: () =>
			versionId
				? DocumentService.getThumbnailByVersion({ id, versionId } as any)
				: DocumentService.getThumbnail({ id } as any),
		enabled: isAuthenticated && Boolean(id),
		retry: 1,
		...options,
	})
}

export const useMetadata = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetFileMetadataDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetFileMetadataDataResponse>({
		queryKey: ['documentMetadata', id],
		queryFn: () => DocumentService.getMetadata({ id } as any),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}

export const useUpdateMetadata = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['updateMetadata'],
		mutationFn: ({
			data,
		}: {
			data: UpdateMetadataProps
			invalidate?: boolean
		}) => DocumentService.updateMetadata(data),
		onError: (error) => {
			console.error(error)
		},
		onSettled: (_, error, { data: { id }, invalidate = true }) => {
			invalidate &&
				queryClient.invalidateQueries({
					queryKey: ['documentMetadata', id],
				})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useMultiClassifyObjects = () => {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationKey: ['classifyMetadataObjects'],
		mutationFn: ({
			data,
		}: {
			data: MultiReclassifyObjectsDataProps
			invalidate?: boolean
			withoutToast?: boolean
		}) => DocumentService.multiClassifyObjects({ data }),
		onSuccess: (_, { withoutToast }) => {
			if (!withoutToast) {
				toastCaller({
					message: 'Reclassification has started in the background...',
					type: 'info',
				})
			}
		},
		onError: (error) => {
			console.error(error)
		},
		onSettled: (_, error, { data, invalidate = true }) => {
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['documentMetadata', data?.File, data?.Folder],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useClassifyFile = () => {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationKey: ['classifyMetadata'],
		mutationFn: ({
			id,
		}: {
			id: string
			invalidate?: boolean
			withoutToast?: boolean
		}) => DocumentService.classifyFile({ id }),
		onSuccess: (_, { withoutToast }) => {
			if (!withoutToast) {
				toastCaller({
					message: 'Reclassification has started in the background...',
					type: 'info',
				})
			}
		},
		onError: (error) => {
			console.error(error)
		},
		onSettled: (_, error, { id, invalidate = true }) => {
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['documentMetadata', id],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useExactFileMetadata = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['classifyMetadata'],
		mutationFn: ({ id }: { id: string; invalidate?: boolean }) =>
			DocumentService.extractFileMetadata({ id }),
		onError: (error) => {
			console.error(error)
		},
		onSettled: (_, error, { id, invalidate = true }) => {
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['documentMetadata', id],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useClassifyFolder = () => {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationKey: ['classifyFolderMetadata'],
		mutationFn: ({
			id,
		}: {
			id: string
			invalidate?: boolean
			withoutToast?: boolean
		}) => DocumentService.classifyFolder({ id }),
		onSuccess: (_, { withoutToast }) => {
			if (!withoutToast) {
				toastCaller({
					message: 'Reclassification has started in the background...',
					type: 'info',
				})
			}
		},
		onError: (error) => {
			console.error(error)
		},
		// onSettled: (_, error, { id, invalidate = true }) => {
		// 	if (invalidate) {
		// 		queryClient.invalidateQueries({
		// 			queryKey: ['documentMetadata', id],
		// 		})
		// 	}
		// },
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

// --- Share file ---
export const useShareFile = () => {
	const mutation = useMutation({
		mutationKey: ['createFile'],
		mutationFn: async ({
			id,
			data,
		}: {
			id: string
			data: ShareFileDataProps
		}) => await DocumentService.shareFile({ id, data }),

		onError: (error, { data }) =>
			toastCaller({
				type: 'warning',
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				errorMessageCode: (error as any)?.response?.data?.errors[0],
			}),
		onSuccess: (_, { data }) => {
			toastCaller({
				type: 'success',
				message: `File was shared successfully`,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			const result = await mutation.mutateAsync(...params)
			return result
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}
export const useSharedPreview = (
	sharedToken: string,
	options?: Partial<QueryObserverOptions<GetSharedPreviewDataResponse>>,
) => {
	return useQuery<GetSharedPreviewDataResponse>({
		queryKey: ['sharedPreview', sharedToken],
		queryFn: () => DocumentService.getSharedPreview(sharedToken),
		enabled: Boolean(sharedToken),
		refetchOnWindowFocus: false,
		retry: 0,
		...options,
	})
}

export const useSharedContent = (
	sharedToken: string,
	options?: Partial<QueryObserverOptions<any>>,
) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return useQuery<any>({
		queryKey: ['sharedPreview', sharedToken],
		queryFn: () => DocumentService.getSharedContent({ sharedToken }),
		enabled: Boolean(sharedToken),
		refetchOnWindowFocus: false,
		...options,
	})
}

export const useDownloadSharedFile = () => {
	const mutation = useMutation({
		mutationKey: ['downloadSharedFile'],
		mutationFn: async (
			data: GetSharedContentProps & { fileName?: string; fileType?: string },
		) => {
			const { fileName, fileType, ...contentProps } = data
			const res = await DocumentService.getSharedContent(contentProps)

			if (!res || !(res instanceof ArrayBuffer) || res.byteLength === 0) {
				throw new Error('File is empty or invalid')
			}

			const mimeType = fileType
				? `application/${fileType}`
				: 'application/octet-stream'
			const blob = new Blob([res], { type: mimeType })
			const url = window.URL.createObjectURL(blob)

			const link = document.createElement('a')
			link.href = url
			link.download = fileName ?? 'Document'
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

// --- Object actions ---

export const useUpdateObjectName = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['updateObjectName'],
		mutationFn: ({
			data,
		}: {
			data: { name: string; type: number; id: string }
			invalidate?: boolean
		}) => DocumentService.updateObjectName({ ...data }),
		onError: (error) => {
			handleApiError(error)
		},
		onSettled: (_, __, { invalidate = true }) => {
			if (invalidate) {
				queryClient.invalidateQueries({ queryKey: ['recent', 'documents'] })
				queryClient.removeQueries({
					queryKey: ['favorites-page'],
					exact: false,
				})
				queryClient.invalidateQueries({ queryKey: ['favorites', 'documents'] })
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useToggleObjectFavorite = () => {
	const queryClient = useQueryClient()
	const { updateEverywhere } = useUpdateDocumentCache()

	const mutation = useMutation({
		mutationKey: ['toggleFavoriteObject'],
		mutationFn: ({
			data,
		}: {
			data: { value: boolean; id: string; type: number }
			invalidate?: boolean
			folderId?: string | null
		}) => DocumentService.toggleObjectFavorite({ ...data }),
		onMutate: async ({ data: { id, value }, folderId }) => {
			await queryClient.cancelQueries({ queryKey: ['recent', 'documents'] })
			await queryClient.cancelQueries({ queryKey: ['favorites', 'documents'] })
			if (folderId) {
				await queryClient.cancelQueries({ queryKey: ['documents', folderId] })
			}
			const previousRecent = queryClient.getQueriesData({
				queryKey: ['recent', 'documents'],
				exact: false,
			})
			const previousFavorites = queryClient.getQueriesData({
				queryKey: ['favorites', 'documents'],
				exact: false,
			})
			const previousFolder =
				folderId != null
					? queryClient.getQueryData<GetFolderDetailsDataResponse>([
							'documents',
							folderId,
						])
					: undefined
			const recentData = previousRecent.find(
				([, d]) => (d as GetRecentFilesResponse)?.data?.length,
			)?.[1] as GetRecentFilesResponse | undefined
			const favoritesData = previousFavorites.find(
				([, d]) => (d as GetDocumentsDataResponse)?.data?.entries?.length,
			)?.[1] as GetDocumentsDataResponse | undefined
			const entry =
				recentData?.data?.find((e) => e.id === id) ??
				favoritesData?.data?.entries?.find((e) => e.id === id) ??
				previousFolder?.data?.items?.entries?.find((e) => e.id === id)
			if (entry) {
				const opts = (
					entry as DocumentData & { options?: { isFavorite?: boolean } }
				).options
				updateEverywhere({
					updatedFile: {
						...entry,
						isFavorite: value,
						...(opts && { options: { ...opts, isFavorite: value } }),
					} as DocumentData,
					folderId: folderId ?? undefined,
				})
			}
			return { previousRecent, previousFavorites, previousFolder, folderId }
		},
		onError: (_err, _variables, context) => {
			const prev = context as
				| {
						previousRecent?: [unknown, unknown][]
						previousFavorites?: [unknown, unknown][]
						previousFolder?: GetFolderDetailsDataResponse
						folderId?: string | null
				  }
				| undefined
			prev?.previousRecent?.forEach(([queryKey, data]) => {
				queryClient.setQueryData(queryKey as unknown[], data)
			})
			prev?.previousFavorites?.forEach(([queryKey, data]) => {
				queryClient.setQueryData(queryKey as unknown[], data)
			})
			if (prev?.folderId != null && prev.previousFolder !== undefined) {
				queryClient.setQueryData(
					['documents', prev.folderId],
					prev.previousFolder,
				)
			}
		},
		onSettled: (_, error, { invalidate = true }) => {
			// Invalidate only on error so refetch restores server state; on success keep optimistic cache to avoid star flickering back
			if (invalidate && error) {
				queryClient.invalidateQueries({ queryKey: ['documents'] })
				queryClient.invalidateQueries({ queryKey: ['recent', 'documents'] })
				queryClient.invalidateQueries({ queryKey: ['favorites', 'documents'] })
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDownloadPreviewLink = () => {
	const mutation = useMutation({
		mutationKey: ['getDownloadLink'],
		mutationFn: ({ id, versionId }: { id: string; versionId?: string }) => {
			if (versionId) {
				return DocumentService.getPreviewByVersion({
					id,
					versionId,
				} as any)
			}
			return DocumentService.getPreview({ id } as any)
		},
		onError: (error) => handleApiError(error),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (
		...params: MutationAsyncParameters
	): Promise<GetPreviewDataResponse | undefined> => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			handleApiError(error)
			return undefined
		}
	}

	return { ...mutation, mutateAsync }
}

export const useFile = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetDocumentDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetDocumentDataResponse>({
		queryKey: id ? ['file', id] : [],
		queryFn: () => DocumentService.getFileById({ id } as any),
		enabled: isAuthenticated && Boolean(id),
		staleTime: 1000 * 5,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		...options,
	})
}

export const useLockFile = () => {
	// const queryClient = useQueryClient()
	const [currentId, setCurrentId] = useState<string | null>(null)

	const mutation = useMutation({
		mutationKey: ['lockFile'],
		mutationFn: ({
			id,
			value,
		}: {
			id: string
			value: boolean
			invalidate?: boolean
		}) => {
			setCurrentId(id)
			return DocumentService.lockFile({ id, value })
		},
		onSuccess: (_, { value }) => {
			toastCaller({
				message: `File ${value ? 'locked' : 'unlocked'}`,
				type: 'success',
			})
		},
		onError: (error) => handleApiError(error),
		onSettled: () => setCurrentId(null),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync, currentId }
}

export const useDeleteObject = () => {
	const queryClient = useQueryClient()
	const socket = useContext(SignalRContext)

	const mutation = useMutation({
		mutationKey: ['deleteObject'],
		mutationFn: ({
			data,
		}: {
			data: { id: string; type: number }
			invalidate?: boolean
			refetch?: () => void
		}) => DocumentService.deleteObject({ ...data }),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({ message: 'Deleted', type: 'success' })
		},
		onSettled: (_, error, { invalidate = true, refetch }) => {
			if (invalidate || !!refetch) {
				refetch?.()
				invalidateDocumentsCache(
					queryClient,
					error ? undefined : socket?.canUseFolderEvents,
				)
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useCopyObject = () => {
	const queryClient = useQueryClient()
	const socket = useContext(SignalRContext)

	const mutation = useMutation({
		mutationKey: ['copyObject'],
		mutationFn: async ({
			data,
			parentId,
			onProgress,
			onErrorCallback,
			autoRename,
		}: {
			data: {
				id: string
				name: string
				type: number
			}
			parentId: string
			invalidate?: boolean
			refetch?: () => void
			onProgress?: (
				itemId: string,
				status: 'processing' | 'completed' | 'failed',
				error?: string,
			) => void
			onErrorCallback?: (error: unknown) => void
			autoRename?: boolean
		}) => {
			onProgress?.(data.id, 'processing')
			return DocumentService.copyObject({ data, parentId })
		},
		onSuccess: (response, { data: originalData, onProgress }) => {
			if (response?.data?.success) {
				onProgress?.(originalData.id, 'completed')
			} else {
				onProgress?.(originalData.id, 'failed', 'Copy operation failed')
			}
		},
		onError: async (
			error,
			{
				data: originalData,
				onProgress,
				parentId,
				autoRename = true,
				onErrorCallback,
			},
		) => {
			let errorMessage: string | undefined = undefined
			let shouldRetry = false

			if (typeof error === 'object' && error !== null) {
				const axiosError = error as any
				const errorCode =
					axiosError?.response?.data?.errors?.[0] ||
					axiosError?.response?.data?.errorMessageCode

				const errorMessageText = axiosError?.response?.data?.message || ''
				const isDuplicateError =
					errorCode === 'FILE_SAME_NAME_EXISTS' ||
					errorCode === 'OBJECT_ALREADY_EXISTS' ||
					errorMessageText.toLowerCase().includes('already exists') ||
					errorMessageText.toLowerCase().includes('file already exists') ||
					errorMessageText.toLowerCase().includes('duplicate')

				if (isDuplicateError && autoRename) {
					shouldRetry = true

					const baseName = originalData.name
					const isFile = originalData.type === 0

					const { getNewUniqueName } =
						await import('../../lib/utils/getUniqueName')

					const targetFolderData =
						queryClient.getQueryData<GetFolderDetailsDataResponse>([
							'documents',
							parentId,
						])
					const existingItems = targetFolderData?.data?.items?.entries || []

					const triedNames = new Set<string>([originalData.name])
					let uniqueName = getNewUniqueName(
						baseName,
						isFile ? 'file' : 'folder',
						existingItems,
					)

					let retryAttempt = 0
					const maxRetries = 10

					while (retryAttempt < maxRetries) {
						try {
							const updatedData = { ...originalData, name: uniqueName }
							const retryResponse = await DocumentService.copyObject({
								data: updatedData,
								parentId,
							})

							if (retryResponse?.data?.success) {
								onProgress?.(originalData.id, 'completed')
								return
							}
						} catch (retryError: any) {
							retryAttempt++

							const retryErrorCode =
								retryError?.response?.data?.errors?.[0] ||
								retryError?.response?.data?.errorMessageCode

							if (
								retryAttempt < maxRetries &&
								(retryErrorCode === 'FILE_SAME_NAME_EXISTS' ||
									retryErrorCode === 'OBJECT_ALREADY_EXISTS' ||
									retryError?.response?.data?.message?.includes(
										'already exists',
									))
							) {
								triedNames.add(uniqueName)

								const updatedItems = [
									...existingItems,
									...Array.from(triedNames).map((name) => ({ name })),
								]

								uniqueName = getNewUniqueName(
									baseName,
									isFile ? 'file' : 'folder',
									updatedItems,
								)

								if (triedNames.has(uniqueName)) {
									let extension = ''
									let nameWithoutExt = uniqueName
									if (isFile) {
										const dotIndex = uniqueName.lastIndexOf('.')
										if (dotIndex !== -1) {
											extension = uniqueName.slice(dotIndex)
											nameWithoutExt = uniqueName.slice(0, dotIndex)
										}
									}
									uniqueName = isFile
										? `${nameWithoutExt} (1)${extension}`
										: `${nameWithoutExt} (1)`
								}

								continue
							}

							errorMessage = 'Copy operation failed after rename attempt'
							break
						}
					}
				}

				if (!shouldRetry) {
					errorMessage = getApiErrorMessage(axiosError)
				}
			}

			if (!shouldRetry || errorMessage) {
				onProgress?.(originalData.id, 'failed', errorMessage)
				onErrorCallback?.(error)
				handleApiError(error)
			}
		},
		onSettled: (_, error, { invalidate = true, refetch }) => {
			if (invalidate || !!refetch) {
				refetch?.()
				invalidateDocumentsCache(
					queryClient,
					error ? undefined : socket?.canUseFolderEvents,
				)
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			const result = await mutation.mutateAsync(...params)
			return result
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useMoveObject = () => {
	const queryClient = useQueryClient()
	const socket = useContext(SignalRContext)

	const mutation = useMutation({
		mutationKey: ['moveObject'],
		mutationFn: async ({
			data,
			parentId,
			onProgress,
			onErrorCallback,
			autoRename,
		}: {
			data: {
				id: string
				name: string
				type: number
			}
			parentId: string
			invalidate?: boolean
			refetch?: () => void
			onProgress?: (
				itemId: string,
				status: 'processing' | 'completed' | 'failed',
				error?: string,
			) => void
			sourceFolderId?: string | null
			onErrorCallback?: (error: unknown) => void
			autoRename?: boolean
		}) => {
			onProgress?.(data.id, 'processing')
			return DocumentService.moveObject({ data, parentId })
		},
		onSuccess: (
			response,
			{ data: originalData, onProgress, sourceFolderId, parentId },
		) => {
			if (response?.data?.success) {
				const finalName = response?.data?.data?.name || originalData.name
				const movedItem = response?.data?.data || {
					...originalData,
					name: finalName,
					parentId,
				}

				onProgress?.(originalData.id, 'completed')

				if (parentId) {
					queryClient.setQueryData<GetFolderDetailsDataResponse>(
						['documents', parentId],
						(oldData) => {
							if (!oldData) return oldData
							const existingEntry = oldData?.data?.items?.entries?.find(
								(entry) => entry.id === originalData.id,
							)
							if (existingEntry) {
								const entries = oldData?.data?.items?.entries?.map((entry) =>
									entry.id === originalData.id
										? { ...entry, ...movedItem }
										: entry,
								)
								return {
									...oldData,
									data: {
										...oldData?.data,
										items: {
											...oldData?.data?.items,
											entries: entries || [],
										},
									},
								}
							} else {
								const entries = [
									...(oldData?.data?.items?.entries || []),
									movedItem,
								]
								return {
									...oldData,
									data: {
										...oldData?.data,
										items: {
											...oldData?.data?.items,
											entries: entries || [],
											pagination: {
												...oldData?.data?.items?.pagination,
												totalCount:
													(oldData?.data?.items?.pagination?.totalCount || 0) +
													1,
											},
										},
									},
								}
							}
						},
					)

					queryClient.setQueryData<GetFolderDetailsDataResponse>(
						['get-folder', parentId],
						(oldData) => {
							if (!oldData) return oldData
							const existingEntry = oldData?.data?.items?.entries?.find(
								(entry) => entry.id === originalData.id,
							)
							if (existingEntry) {
								const entries = oldData?.data?.items?.entries?.map((entry) =>
									entry.id === originalData.id
										? { ...entry, ...movedItem }
										: entry,
								)
								return {
									...oldData,
									data: {
										...oldData?.data,
										items: {
											...oldData?.data?.items,
											entries: entries || [],
										},
									},
								}
							} else {
								const entries = [
									...(oldData?.data?.items?.entries || []),
									movedItem,
								]
								return {
									...oldData,
									data: {
										...oldData?.data,
										items: {
											...oldData?.data?.items,
											entries: entries || [],
											pagination: {
												...oldData?.data?.items?.pagination,
												totalCount:
													(oldData?.data?.items?.pagination?.totalCount || 0) +
													1,
											},
										},
									},
								}
							}
						},
					)
				}

				if (sourceFolderId && sourceFolderId !== parentId) {
					queryClient.setQueryData<GetFolderDetailsDataResponse>(
						['documents', sourceFolderId],
						(oldData) => {
							if (!oldData) return oldData
							const entries = oldData?.data?.items?.entries?.filter(
								(entry) => entry.id !== originalData.id,
							)
							return {
								...oldData,
								data: {
									...oldData?.data,
									items: {
										...oldData?.data?.items,
										entries: entries || [],
										pagination: {
											...oldData?.data?.items?.pagination,
											totalCount: Math.max(
												0,
												(oldData?.data?.items?.pagination?.totalCount || 1) - 1,
											),
										},
									},
								},
							}
						},
					)

					queryClient.setQueryData<GetFolderDetailsDataResponse>(
						['get-folder', sourceFolderId],
						(oldData) => {
							if (!oldData) return oldData
							const entries = oldData?.data?.items?.entries?.filter(
								(entry) => entry.id !== originalData.id,
							)
							return {
								...oldData,
								data: {
									...oldData?.data,
									items: {
										...oldData?.data?.items,
										entries: entries || [],
										pagination: {
											...oldData?.data?.items?.pagination,
											totalCount: Math.max(
												0,
												(oldData?.data?.items?.pagination?.totalCount || 1) - 1,
											),
										},
									},
								},
							}
						},
					)
				}
			} else {
				onProgress?.(originalData.id, 'failed', 'Move operation failed')
			}
		},
		onError: async (
			error,
			{
				data: originalData,
				onProgress,
				parentId,
				autoRename = true,
				onErrorCallback,
			},
		) => {
			let errorMessage: string | undefined = undefined
			let shouldRetry = false

			if (typeof error === 'object' && error !== null) {
				const axiosError = error as any
				const errorCode =
					axiosError?.response?.data?.errors?.[0] ||
					axiosError?.response?.data?.errorMessageCode

				const errorMessageText = axiosError?.response?.data?.message || ''
				const isDuplicateError =
					errorCode === 'FILE_SAME_NAME_EXISTS' ||
					errorCode === 'OBJECT_ALREADY_EXISTS' ||
					errorMessageText.toLowerCase().includes('already exists') ||
					errorMessageText.toLowerCase().includes('file already exists') ||
					errorMessageText.toLowerCase().includes('duplicate')

				if (isDuplicateError && autoRename) {
					shouldRetry = true

					const baseName = originalData.name
					const isFile = originalData.type === 0

					const { getNewUniqueName } =
						await import('../../lib/utils/getUniqueName')

					const targetFolderData =
						queryClient.getQueryData<GetFolderDetailsDataResponse>([
							'documents',
							parentId,
						])
					const existingItems = targetFolderData?.data?.items?.entries || []

					const triedNames = new Set<string>([originalData.name])
					let uniqueName = getNewUniqueName(
						baseName,
						isFile ? 'file' : 'folder',
						existingItems,
					)

					let retryAttempt = 0
					const maxRetries = 10

					while (retryAttempt < maxRetries) {
						try {
							const updatedData = { ...originalData, name: uniqueName }
							const retryResponse = await DocumentService.moveObject({
								data: updatedData,
								parentId,
							})

							if (retryResponse?.data?.success) {
								onProgress?.(originalData.id, 'completed')
								return
							}
						} catch (retryError: any) {
							retryAttempt++

							const retryErrorCode =
								retryError?.response?.data?.errors?.[0] ||
								retryError?.response?.data?.errorMessageCode

							if (
								retryAttempt < maxRetries &&
								(retryErrorCode === 'FILE_SAME_NAME_EXISTS' ||
									retryErrorCode === 'OBJECT_ALREADY_EXISTS' ||
									retryError?.response?.data?.message?.includes(
										'already exists',
									))
							) {
								triedNames.add(uniqueName)

								const updatedItems = [
									...existingItems,
									...Array.from(triedNames).map((name) => ({ name })),
								]

								uniqueName = getNewUniqueName(
									baseName,
									isFile ? 'file' : 'folder',
									updatedItems,
								)

								if (triedNames.has(uniqueName)) {
									let extension = ''
									let nameWithoutExt = uniqueName
									if (isFile) {
										const dotIndex = uniqueName.lastIndexOf('.')
										if (dotIndex !== -1) {
											extension = uniqueName.slice(dotIndex)
											nameWithoutExt = uniqueName.slice(0, dotIndex)
										}
									}
									uniqueName = isFile
										? `${nameWithoutExt} (1)${extension}`
										: `${nameWithoutExt} (1)`
								}

								continue
							}

							errorMessage = 'Move operation failed after rename attempt'
							break
						}
					}
				}

				if (!shouldRetry) {
					errorMessage = getApiErrorMessage(axiosError)
				}
			}

			if (!shouldRetry || errorMessage) {
				onProgress?.(originalData.id, 'failed', errorMessage)
				onErrorCallback?.(error)
				handleApiError(error)
			}
		},
		onSettled: (_, error, { invalidate = true, refetch }) => {
			if (invalidate || !!refetch) {
				refetch?.()
				invalidateDocumentsCache(
					queryClient,
					error ? undefined : socket?.canUseFolderEvents,
				)
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			const result = await mutation.mutateAsync(...params)
			return result
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

// --- Files' system ---
export const useDeleteObjects = () => {
	const queryClient = useQueryClient()
	const socket = useContext(SignalRContext)

	const mutation = useMutation({
		mutationKey: ['deleteObjects'],
		mutationFn: ({
			data,
		}: {
			data: { id: string; type: number }[]
			invalidate?: boolean
			refetch?: () => void
		}) => DocumentService.deleteObjects({ data }),
		onSuccess: (_, { data }) =>
			toastCaller({
				type: 'success',
				message: `${data?.length} items was deleted`,
			}),
		onError: (error) => handleApiError(error),
		onSettled: (_, error, { invalidate = true, refetch }) => {
			if (invalidate || !!refetch) {
				refetch?.()
				invalidateDocumentsCache(
					queryClient,
					error ? undefined : socket?.canUseFolderEvents,
				)
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useCopyObjects = () => {
	const queryClient = useQueryClient()
	const socket = useContext(SignalRContext)

	const mutation = useMutation({
		mutationKey: ['copyObjects'],
		mutationFn: ({
			data,
			parentId,
			onProgress,
		}: {
			data: {
				id: string
				type: number
				name: string
			}[]
			parentId: string
			invalidate?: boolean
			refetch?: () => void
			onProgress?: (
				itemId: string,
				status: 'processing' | 'completed' | 'failed',
				error?: string,
			) => void
		}) => {
			data.forEach((item) => {
				onProgress?.(item.id, 'processing')
			})

			return DocumentService.copyObjects({ data, parentId })
		},
		onSuccess: (response, { data, onProgress }) => {
			const responseData = response?.data?.data || response?.data
			const failedItems = responseData?.failedItems || responseData?.failed
			const successfulItems =
				responseData?.successfulItems || responseData?.successful

			if (response?.data?.success) {
				if (
					failedItems &&
					Array.isArray(failedItems) &&
					failedItems.length > 0
				) {
					failedItems.forEach((itemId: string) => {
						onProgress?.(itemId, 'failed', 'Copy operation failed')
					})
					if (successfulItems && Array.isArray(successfulItems)) {
						successfulItems.forEach((itemId: string) => {
							onProgress?.(itemId, 'completed')
						})
					} else {
						data.forEach((item) => {
							if (!failedItems.includes(item.id)) {
								onProgress?.(item.id, 'completed')
							}
						})
					}
				} else {
					data.forEach((item) => {
						onProgress?.(item.id, 'completed')
					})
				}
			} else {
				if (failedItems && Array.isArray(failedItems)) {
					failedItems.forEach((itemId: string) => {
						onProgress?.(itemId, 'failed', 'Copy operation failed')
					})
					if (successfulItems && Array.isArray(successfulItems)) {
						successfulItems.forEach((itemId: string) => {
							onProgress?.(itemId, 'completed')
						})
					}
				} else {
					data.forEach((item) => {
						onProgress?.(item.id, 'failed', 'Copy operation failed')
					})
				}
			}
		},
		onError: (error, { data, onProgress }) => {
			let errorMessage: string | undefined = undefined
			let failedItemIds: string[] | undefined = undefined

			if (typeof error === 'object' && error !== null) {
				const axiosError = error as any
				if (axiosError?.response?.data?.failedItems) {
					failedItemIds = axiosError.response.data.failedItems
				} else if (axiosError?.response?.data?.failed) {
					failedItemIds = Array.isArray(axiosError.response.data.failed)
						? axiosError.response.data.failed
						: undefined
				}

				if (axiosError?.response?.data?.errorMessageCode) {
					errorMessage = axiosError.response.data.errorMessageCode
				} else if (axiosError?.response?.data?.message) {
					errorMessage = axiosError.response.data.message
				} else if (axiosError?.message) {
					errorMessage = axiosError.message
				}
			}

			if (failedItemIds && Array.isArray(failedItemIds)) {
				failedItemIds.forEach((itemId) => {
					onProgress?.(itemId, 'failed', errorMessage)
				})
				data.forEach((item) => {
					if (!failedItemIds?.includes(item.id)) {
						onProgress?.(item.id, 'completed')
					}
				})
			}
		},
		onSettled: (_, error, { invalidate = true, refetch }) => {
			if (invalidate || !!refetch) {
				refetch?.()
				invalidateDocumentsCache(
					queryClient,
					error ? undefined : socket?.canUseFolderEvents,
				)
				queryClient.invalidateQueries({
					queryKey: ['trash'],
					exact: false,
				})
				queryClient.invalidateQueries({
					queryKey: ['trash-page'],
					exact: false,
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useMoveObjects = () => {
	const queryClient = useQueryClient()
	const socket = useContext(SignalRContext)

	const mutation = useMutation({
		mutationKey: ['moveObjects'],
		mutationFn: ({
			data,
			parentId,
			onProgress,
			sourceFolderId,
		}: {
			data: {
				id: string
				type: number
				name: string
			}[]
			parentId: string
			invalidate?: boolean
			refetch?: () => void
			onProgress?: (
				itemId: string,
				status: 'processing' | 'completed' | 'failed',
				error?: string,
			) => void
			sourceFolderId?: string | null
		}) => {
			data.forEach((item) => {
				onProgress?.(item.id, 'processing')
			})

			return DocumentService.moveObjects({ data, parentId })
		},
		onSuccess: (response, { data, onProgress, sourceFolderId }) => {
			const responseData = response?.data?.data || response?.data
			const failedItems = responseData?.failedItems || responseData?.failed
			const successfulItems =
				responseData?.successfulItems || responseData?.successful

			if (response?.data?.success) {
				let actuallyMovedIds: string[] = []

				if (
					failedItems &&
					Array.isArray(failedItems) &&
					failedItems.length > 0
				) {
					if (successfulItems && Array.isArray(successfulItems)) {
						actuallyMovedIds = successfulItems
					} else {
						actuallyMovedIds = data
							.map((item) => item.id)
							.filter((id) => !failedItems.includes(id))
					}

					failedItems.forEach((itemId: string) => {
						onProgress?.(itemId, 'failed', 'Move operation failed')
					})
					actuallyMovedIds.forEach((itemId: string) => {
						onProgress?.(itemId, 'completed')
					})
				} else {
					actuallyMovedIds = data.map((item) => item.id)
					data.forEach((item) => {
						onProgress?.(item.id, 'completed')
					})
				}

				if (sourceFolderId && actuallyMovedIds.length > 0) {
					queryClient.setQueryData<GetFolderDetailsDataResponse>(
						['documents', sourceFolderId],
						(oldData) => {
							if (!oldData) return oldData
							const entries = oldData?.data?.items?.entries?.filter(
								(entry) => !actuallyMovedIds.includes(entry.id),
							)
							return {
								...oldData,
								data: {
									...oldData?.data,
									items: {
										...oldData?.data?.items,
										entries: entries || [],
										pagination: {
											...oldData?.data?.items?.pagination,
											totalCount: Math.max(
												0,
												(oldData?.data?.items?.pagination?.totalCount ||
													actuallyMovedIds.length) - actuallyMovedIds.length,
											),
										},
									},
								},
							}
						},
					)

					queryClient.setQueryData<GetFolderDetailsDataResponse>(
						['get-folder', sourceFolderId],
						(oldData) => {
							if (!oldData) return oldData
							const entries = oldData?.data?.items?.entries?.filter(
								(entry) => !actuallyMovedIds.includes(entry.id),
							)
							return {
								...oldData,
								data: {
									...oldData?.data,
									items: {
										...oldData?.data?.items,
										entries: entries || [],
										pagination: {
											...oldData?.data?.items?.pagination,
											totalCount: Math.max(
												0,
												(oldData?.data?.items?.pagination?.totalCount ||
													actuallyMovedIds.length) - actuallyMovedIds.length,
											),
										},
									},
								},
							}
						},
					)
				}
			} else {
				if (failedItems && Array.isArray(failedItems)) {
					failedItems.forEach((itemId: string) => {
						onProgress?.(itemId, 'failed', 'Move operation failed')
					})
					if (successfulItems && Array.isArray(successfulItems)) {
						successfulItems.forEach((itemId: string) => {
							onProgress?.(itemId, 'completed')
						})
					}
				} else {
					data.forEach((item) => {
						onProgress?.(item.id, 'failed', 'Move operation failed')
					})
				}
			}
		},
		onError: (error, { data, onProgress }) => {
			let errorMessage: string | undefined = undefined
			let failedItemIds: string[] | undefined = undefined

			if (typeof error === 'object' && error !== null) {
				const axiosError = error as any
				if (axiosError?.response?.data?.failedItems) {
					failedItemIds = axiosError.response.data.failedItems
				} else if (axiosError?.response?.data?.failed) {
					failedItemIds = Array.isArray(axiosError.response.data.failed)
						? axiosError.response.data.failed
						: undefined
				}

				if (axiosError?.response?.data?.errorMessageCode) {
					errorMessage = axiosError.response.data.errorMessageCode
				} else if (axiosError?.response?.data?.message) {
					errorMessage = axiosError.response.data.message
				}
			}

			if (failedItemIds && Array.isArray(failedItemIds)) {
				failedItemIds.forEach((itemId) => {
					onProgress?.(itemId, 'failed', errorMessage)
				})
				data.forEach((item) => {
					if (!failedItemIds?.includes(item.id)) {
						onProgress?.(item.id, 'completed')
					}
				})
			}
		},
		onSettled: (_, error, { invalidate = true, refetch }) => {
			if (invalidate || !!refetch) {
				refetch?.()
				invalidateDocumentsCache(
					queryClient,
					error ? undefined : socket?.canUseFolderEvents,
				)
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useToggleObjectsFavorite = () => {
	const mutation = useMutation({
		mutationKey: ['toggleObjectsFavorite'],
		mutationFn: ({
			data,
		}: {
			data: { ids: string[]; value: boolean }
			invalidate?: boolean
		}) => DocumentService.toggleObjectsFavorite({ ...data }),
		onSuccess: (_, { data }) => {
			toastCaller({
				type: 'success',
				message: `${data?.ids?.length} item${data?.ids?.length > 1 ? 's' : ''} was ${data?.value ? 'added to' : 'deleted from'} favorites`,
			})
		},
		onError: (error) => {
			handleApiError(error)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

// --- Files' system search ---
export const useSearch = (
	{ ...params }: GetSearchParamsProps,
	options?: Partial<QueryObserverOptions<GetSearchData>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetSearchData>({
		queryKey: ['quick-search', params?.query, params?.startId],
		queryFn: () => DocumentService.search({ ...params }),
		enabled: isAuthenticated,
		gcTime: 1000 * 60 * 5,
		retry: 0,
		...options,
	})
}

export const useSearchAi = (
	{ ...params }: GetSearchParamsProps,
	options?: Partial<QueryObserverOptions<GetSearchData>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetSearchData>({
		queryKey: ['ai-search', params?.query, params?.startId],
		queryFn: () => DocumentService.searchAi({ ...params }),
		enabled: isAuthenticated,
		gcTime: 1000 * 60 * 5,
		retry: 0,
		...options,
	})
}

// export const useSearchFullText = (
// 	{ ...params }: GetSearchParamsProps,
// 	options?: Partial<QueryObserverOptions<GetSearchTextData>>
// ) => {
// 	return useQuery<GetSearchTextData>({
// 		queryKey: ['search-full-text'],
// 		queryFn: () => DocumentService.searchFullText({ ...params}),
// 		enabled: isAuthenticated,
// 		staleTime: 1000 * 5,
// 		retry: 1,
// 		...options,
// 	})
// }

// Download objects
export const useDownloadObjects = () => {
	const mutation = useMutation({
		mutationKey: ['downloadObjects'],
		mutationFn: ({ objects }: { objects: string[] }) =>
			DocumentService.downloadObjects({ objects }),
		onSuccess: () => {
			// queryClient.invalidateQueries('task-status')
		},
		onError: (error) => handleApiError(error),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useCancelDownload = () => {
	const mutation = useMutation({
		mutationKey: ['cancelDownload'],
		mutationFn: ({ taskId }: { taskId: string }) =>
			TaskService.cancelDownload({ taskId }),
		onError: (error) => {
			console.error('Cancel download error on server:', error)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useFavorites = (
	{
		offset = 0,
		limit = 10,
		columnFilters,
		...params
	}: Omit<TableDataDefaultGetParamsProps, 'id'>,
	options?: Partial<QueryObserverOptions<GetDocumentsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const queryClient = useQueryClient()

	const pageSize = limit * 3
	const forcedOffset = Math.floor(offset / pageSize) * pageSize

	const queryKey = [
		'favorites',
		'documents',
		offset,
		limit,
		JSON.stringify(columnFilters),
		JSON.stringify(params),
	]

	const pageKey = [
		'favorites-page',
		forcedOffset,
		pageSize,
		JSON.stringify(columnFilters),
		JSON.stringify(params),
	]

	const cachedPage = queryClient.getQueryData<GetDocumentsDataResponse>(pageKey)

	const initialData = cachedPage
		? {
				...cachedPage,
				data: {
					...cachedPage.data,
					entries: cachedPage.data.entries.slice(
						offset - forcedOffset,
						offset - forcedOffset + limit,
					),
					pagination: {
						...cachedPage.data.pagination,
						offset,
						limit,
					},
				},
			}
		: undefined

	const queryFn = async () => {
		const page = queryClient.getQueryData<GetDocumentsDataResponse>(pageKey)
		if (page) {
			return {
				...page,
				data: {
					...page.data,
					entries: page.data.entries.slice(
						offset - forcedOffset,
						offset - forcedOffset + limit,
					),
					pagination: {
						...page.data.pagination,
						offset,
						limit,
					},
				},
			}
		}
		const fresh = await DocumentService.getFavorites({
			offset: forcedOffset,
			limit: pageSize,
			columnFilters,
			...params,
		})
		queryClient.setQueryData(pageKey, fresh)
		return {
			...fresh,
			data: {
				...fresh.data,
				entries: fresh?.data?.entries?.slice(
					offset - forcedOffset,
					offset - forcedOffset + limit,
				),
				pagination: {
					...fresh.data.pagination,
					offset,
					limit,
				},
			},
		}
	}

	const queryResult = useQuery<GetDocumentsDataResponse>({
		queryKey,
		enabled: isAuthenticated,
		queryFn,
		initialData,
		placeholderData: () => initialData,
		...options,
	})

	const hardFetch = async (currentOffset = offset) => {
		const groupOffset = Math.floor(currentOffset / pageSize) * pageSize
		const fresh = await DocumentService.getFavorites({
			offset: groupOffset,
			limit: pageSize,
			columnFilters,
			...params,
		})
		const groupKey = [
			'favorites-page',
			groupOffset,
			pageSize,
			JSON.stringify(columnFilters),
			JSON.stringify(params),
		]
		queryClient.setQueryData(groupKey, fresh)
		const view = {
			...fresh,
			data: {
				...fresh.data,
				entries: fresh?.data?.entries?.slice(
					currentOffset - groupOffset,
					currentOffset - groupOffset + limit,
				),
				pagination: {
					...fresh.data.pagination,
					offset: currentOffset,
					limit,
				},
			},
		}
		queryClient.setQueryData(queryKey, view)
		return view
	}

	const buildView = (
		fresh: GetDocumentsDataResponse,
		currentOffset: number,
		groupOffset: number,
	) => ({
		...fresh,
		data: {
			...fresh.data,
			entries: fresh.data.entries.slice(
				currentOffset - groupOffset,
				currentOffset - groupOffset + limit,
			),
			pagination: {
				...fresh.data.pagination,
				offset: currentOffset,
				limit,
			},
		},
	})

	const updateItems = async ({
		forceRefetch = false,
		offset: newOffset,
	}: { forceRefetch?: boolean; offset?: number } = {}) => {
		const currentOffset = typeof newOffset === 'number' ? newOffset : offset
		const currentGroupOffset = Math.floor(currentOffset / pageSize) * pageSize
		const currentPageKey = [
			'favorites-page',
			currentGroupOffset,
			pageSize,
			JSON.stringify(columnFilters),
			JSON.stringify(params),
		]

		if (forceRefetch) {
			queryClient.removeQueries({ queryKey: currentPageKey, exact: true })

			const fresh = await DocumentService.getFavorites({
				offset: currentGroupOffset,
				limit: pageSize,
				columnFilters,
				...params,
			})
			queryClient.setQueryData(currentPageKey, fresh)
			const view = buildView(fresh, currentOffset, currentGroupOffset)
			queryClient.setQueryData(queryKey, view)
			return view
		}
		return queryClient.fetchQuery({
			queryKey,
			queryFn: () => queryFn(),
			staleTime: 0,
		})
	}

	useEffect(() => {
		if (!isAuthenticated || !cachedPage) return
		hardFetch(offset).catch(() => {})
	}, [])

	return { ...queryResult, updateItems }
}

// --- Trash items ---
export const useTrashDocuments = (
	{
		offset = 0,
		limit = 10,
		columnFilters,
		...params
	}: Omit<TableDataDefaultGetParamsProps, 'id'>,
	options?: Partial<QueryObserverOptions<GetDeletedDocumentsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const queryClient = useQueryClient()

	const pageSize = limit * 3
	const forcedOffset = Math.floor(offset / pageSize) * pageSize

	const pageKey = [
		'trash-page',
		forcedOffset,
		pageSize,
		JSON.stringify(columnFilters),
		JSON.stringify(params),
	]

	const queryKey = [
		'trash',
		offset,
		limit,
		JSON.stringify(columnFilters),
		JSON.stringify(params),
	]

	const cachedPage =
		queryClient.getQueryData<GetDeletedDocumentsDataResponse>(pageKey)

	const initialData = cachedPage
		? {
				...cachedPage,
				data: {
					...cachedPage.data,
					entries: cachedPage.data.entries.slice(
						offset - forcedOffset,
						offset - forcedOffset + limit,
					),
					pagination: {
						...cachedPage.data.pagination,
						offset,
						limit,
					},
				},
			}
		: undefined

	const queryFn = async () => {
		if (cachedPage) {
			return {
				...cachedPage,
				data: {
					...cachedPage.data,
					entries: cachedPage.data.entries.slice(
						offset - forcedOffset,
						offset - forcedOffset + limit,
					),
					pagination: {
						...cachedPage.data.pagination,
						offset,
						limit,
					},
				},
			}
		}
		const fresh = await DocumentService.getTrashDocuments({
			offset: forcedOffset,
			limit: pageSize,
			columnFilters,
			...params,
		})
		queryClient.setQueryData(pageKey, fresh)
		return {
			...fresh,
			data: {
				...fresh.data,
				entries: fresh?.data?.entries?.slice(
					offset - forcedOffset,
					offset - forcedOffset + limit,
				),
				pagination: {
					...fresh.data.pagination,
					offset,
					limit,
				},
			},
		}
	}

	const queryResult = useQuery<GetDeletedDocumentsDataResponse>({
		queryKey,
		enabled: isAuthenticated,
		queryFn,
		initialData,
		placeholderData: () => initialData,
		...options,
	})

	const forceUpdate = async () => {
		const fresh = await DocumentService.getTrashDocuments({
			offset: forcedOffset,
			limit: pageSize,
			columnFilters,
			...params,
		})
		queryClient.setQueryData(pageKey, fresh)
		queryClient.setQueryData(queryKey, {
			...fresh,
			data: {
				...fresh.data,
				entries: fresh?.data?.entries?.slice(
					offset - forcedOffset,
					offset - forcedOffset + limit,
				),
				pagination: {
					...fresh.data.pagination,
					offset,
					limit,
				},
			},
		})
		return fresh
	}

	return { ...queryResult, forceUpdate }
}

export const useDeleteObjectFromTrash = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['deleteObjectFromTrash'],
		mutationFn: ({ id }: { id: string; invalidate?: boolean }) =>
			DocumentService.deleteObjectFromTrash({ id }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { invalidate }) => {
			toastCaller({ message: 'Deleted', type: 'success' })
			if (invalidate) {
				queryClient.invalidateQueries({ queryKey: ['trash'], exact: false })
				queryClient.invalidateQueries({
					queryKey: ['trash-page'],
					exact: false,
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useRestoreObjectFromTrash = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['restoreObjectFromTrash'],
		mutationFn: ({ id }: { id: string; invalidate?: boolean }) =>
			DocumentService.restoreObjectFromTrash({ id }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { invalidate }) => {
			toastCaller({ message: 'Restored', type: 'success' })
			if (invalidate) {
				queryClient.removeQueries({ queryKey: ['trash'], exact: false })
				queryClient.removeQueries({ queryKey: ['trash-page'], exact: false })
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteObjectsFromTrash = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['deleteObjectsFromTrash'],
		mutationFn: ({ objects }: { objects: string[]; invalidate?: boolean }) =>
			DocumentService.deleteObjectsFromTrash({ objects }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { objects, invalidate }) => {
			toastCaller({
				message: `Deleted ${objects?.length} objects`,
				type: 'success',
			})
			if (invalidate) {
				queryClient.removeQueries({ queryKey: ['trash'], exact: false })
				queryClient.removeQueries({ queryKey: ['trash-page'], exact: false })
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useRestoreObjectsFromTrash = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['restoreObjectsFromTrash'],
		mutationFn: ({ objects }: { objects: string[]; invalidate?: boolean }) =>
			DocumentService.restoreObjectsFromTrash({ objects }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { objects, invalidate }) => {
			toastCaller({
				message: `Restored ${objects?.length} objects`,
				type: 'success',
			})
			if (invalidate) {
				queryClient.removeQueries({ queryKey: ['trash'], exact: false })
				queryClient.removeQueries({ queryKey: ['trash-page'], exact: false })
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useClearTrash = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['clearTrash'],
		mutationFn: ({ invalidate }: { invalidate?: boolean } = {}) =>
			DocumentService.clearTrash(),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { invalidate }) => {
			toastCaller({
				message: 'Trash cleared',
				type: 'success',
			})
			if (invalidate) {
				queryClient.removeQueries({ queryKey: ['trash'], exact: false })
				queryClient.removeQueries({ queryKey: ['trash-page'], exact: false })
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

// --- Versions Config ---
export const useDeleteObjectVersion = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['document-version', 'delete'],
		mutationFn: (data: DefaultGetParamsProps & { versionId: string }) =>
			DocumentService.deleteObjectVersion(data),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({ message: 'Version deleted', type: 'success' })
			queryClient.invalidateQueries({
				queryKey: ['file'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useRollbackObjectVersion = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['document-version', 'rollback'],
		mutationFn: (data: DefaultGetParamsProps & { versionId: string }) =>
			DocumentService.rollbackObjectVersion(data),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({ message: 'Current version changed', type: 'success' })
			queryClient.invalidateQueries({
				queryKey: ['file'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}
